import { Attribute } from '@/api/AttributeApi';
import { CreatorState } from '@/types/creator';
import { syncEffect } from '@/utils/creatorUtils';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { RootState, AppDispatch } from '..';
import { creatorActions } from '../slices/creatorSlice';
import ProductApi, { ProductAttribute } from '@/api/ProductApi';

export const creatorMiddleware = createListenerMiddleware();

creatorMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.updateDimensions,
	effect: async ({ payload }, listenerApi) => {
		const { product, dimensionConfigValues, clientId } =
			listenerApi.getState().creator;
		if (!product) return;

		listenerApi.cancelActiveListeners();

		await listenerApi.delay(1000);

		const {
			price,
			netPrice,
			discountedPrice,
			discountedNetPrice,
			dimensions,
			weight,
			surface,
		} = await ProductApi.updateDimensions({
			id: product.id,
			dimensions: Object.entries(dimensionConfigValues).map(
				([shortName, values]) => ({
					shortName,
					...values,
				})
			),
			clientId,
		});

		listenerApi.dispatch(
			creatorActions.updateProduct({
				price,
				netPrice,
				discountedPrice,
				discountedNetPrice,
				weight,
				surface,
			})
		);

		listenerApi.dispatch(
			creatorActions.setDimensions(
				dimensions.reduce<CreatorState['dimensionConfigValues']>(
					(acc, curr) => {
						acc[curr.shortName] = {
							width: curr.width,
							height: curr.height,
						};
						return acc;
					},
					{}
				)
			)
		);
	},
});

creatorMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.setProductAttributeValue,
	effect: async ({ payload }, listenerApi) => {
		const {
			product,
			chosenProductAttributes: chosenAnswers,
			clientId,
		} = listenerApi.getState().creator;
		if (!product) return;

		listenerApi.cancelActiveListeners();

		await listenerApi.delay(1000);

		const productAttributes = Object.values(chosenAnswers).reduce<
			ProductAttribute[]
		>((acc, attributeAnswers) => {
			for (const [attributeId, answer] of Object.entries(attributeAnswers)) {
				acc.push({
					attribute: { id: attributeId } as Attribute,
					componentAttributeValue: answer.componentAttributeValue,
					value: answer.value,
				});
			}
			return acc;
		}, []);

		await ProductApi.update({
			id: product.id,
			productAttributes,
			clientId,
		});
	},
});

creatorMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.addAccessory,
	effect: async ({ payload }, listenerApi) => {
		const { product, chosenAccessories, clientId } =
			listenerApi.getState().creator;
		if (!product) return;

		listenerApi.cancelActiveListeners();

		await listenerApi.delay(500);

		const accessories = Object.values(chosenAccessories);

		const {
			price,
			netPrice,
			discountedPrice,
			discountedNetPrice,
		} = await ProductApi.update({
			id: product.id,
			accessories,
			clientId,
		});

		listenerApi.dispatch(
			creatorActions.updateProduct({
				price,
				netPrice,
				discountedPrice,
				discountedNetPrice,
			})
		);
	},
});

creatorMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.updateProductWithSync,
	effect: async ({ payload }, listenerApi) => {
		const { product, clientId } = listenerApi.getState().creator;
		if (!product) return;

		listenerApi.cancelActiveListeners();

		await listenerApi.delay(1000);

		await ProductApi.update({
			id: product.id,
			...payload,
			clientId,
		});
	},
});

creatorMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.selectProductAttributeValue,
	effect: syncEffect,
});

creatorMiddleware.startListening.withTypes<RootState, AppDispatch>()({
	actionCreator: creatorActions.filterAttributes,
	effect: syncEffect,
});
